import { TypedDocumentNode, useQuery } from "urql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PaginatedDataAccessResult } from "@acto/taskpane/core/data-access/types";
import { isFetchingResult } from "@acto/taskpane/core/data-access/isFetchingResult";
import type { Edge, Node, PageInfo } from "@acto/gql/graphql";

interface Connection<TEdge extends Pick<Edge, "node">> {
  edges: TEdge[];
  pageInfo: PageInfo;
}

/**
 * @template TQueryData - Raw data returned by the query
 * @template TVariables - Full query variables (must include first/after)
 * @template TNode - Node type in the connection (must have an id)
 * @template TMetadata - Optional connection-level metadata (e.g. totals)
 */
interface UsePaginationOptions<
  TQueryData,
  TVariables extends { first: number; after?: string | null },
  TNode extends Node,
  TMetadata = undefined,
> {
  query: TypedDocumentNode<TQueryData, TVariables>;
  limit: number;
  variables?: Omit<TVariables, "first" | "after">;
  enabled?: boolean;
  /** Selects the connection from the query data, e.g. `(data) => data.todos`. */
  connection: (data: TQueryData) => Connection<{ node: TNode }>;
  /** Selects connection-level metadata (e.g. totals), constant across pages of one scope. */
  metadata?: (data: TQueryData) => TMetadata | undefined;
}

interface PageVariables {
  first: number;
  after: string | null;
}

const MAX_REFILL_ATTEMPTS = 3;

/** Serializes a query's scope: its variables minus the page cursor (`first`/`after`). */
function toScopeKey(variables: object): string {
  const withoutCursor = Object.fromEntries(
    Object.entries(variables).filter(([key]) => key !== "first" && key !== "after"),
  );
  return JSON.stringify(withoutCursor);
}

export function usePagination<
  TQueryData,
  TVariables extends { first: number; after?: string | null },
  TNode extends Node,
  TMetadata = undefined,
>({
  query,
  variables,
  limit,
  enabled = true,
  connection: getConnection,
  metadata: getMetadata,
}: UsePaginationOptions<TQueryData, TVariables, TNode, TMetadata>): PaginatedDataAccessResult<TNode, TMetadata> {
  // A page request = scope (which dataset: client, dateRange, filters) + cursor (first/after).
  // Accumulation resets when the scope changes; the cursor just advances within it.
  const scopeVariables = useMemo(() => (variables ?? {}) as Omit<TVariables, "first" | "after">, [variables]);
  const scopeKey = useMemo(() => toScopeKey(scopeVariables), [scopeVariables]);
  const resetKey = `${limit}|${scopeKey}`;

  const [pages, setPages] = useState<PageVariables[]>([{ first: limit, after: null }]);
  const [allData, setAllData] = useState<TNode[]>([]);
  const [refillAttempts, setRefillAttempts] = useState(0);
  const [renderedResetKey, setRenderedResetKey] = useState(resetKey);
  const lastMetadataRef = useRef<TMetadata | undefined>(undefined);

  // Reset accumulation synchronously when the scope (connection variables or page size) changes, so a
  // switch never shows the previous scope's rows/total for a frame. Adjusting state during render is
  // React's idiom for this (https://react.dev/learn/you-might-not-need-an-effect).
  if (renderedResetKey !== resetKey) {
    setRenderedResetKey(resetKey);
    setPages([{ first: limit, after: null }]);
    setAllData([]);
    setRefillAttempts(0);
    lastMetadataRef.current = undefined;
  }

  const currentVariables = pages[pages.length - 1];
  const queryVariables = useMemo(
    () => ({ ...scopeVariables, first: currentVariables.first, after: currentVariables.after }) as TVariables,
    [scopeVariables, currentVariables.first, currentVariables.after],
  );

  const [result, reexecuteQuery] = useQuery({
    query,
    variables: queryVariables,
    requestPolicy: "cache-first",
    pause: !enabled,
  });

  // urql can briefly report the previous operation's result right after `variables` change. Ignore it
  // so we never merge or display another scope's data.
  const resultMatchesScope = !result.operation || toScopeKey(result.operation.variables) === scopeKey;
  const data = resultMatchesScope ? result.data : undefined;
  const error = resultMatchesScope ? result.error : undefined;
  const isFetching = isFetchingResult(result);

  // Cache the extractor's result by the urql `data` reference: extractors that allocate (spreading
  // edge fields onto nodes) would otherwise return a fresh connection every render and re-trigger the
  // accumulation effect endlessly.
  const connectionCacheRef = useRef<{ source: TQueryData; result: Connection<{ node: TNode }> } | null>(null);
  let connection: Connection<{ node: TNode }> | null = null;
  if (enabled && data) {
    if (connectionCacheRef.current?.source === data) {
      connection = connectionCacheRef.current.result;
    } else {
      connection = getConnection(data);
      connectionCacheRef.current = { source: data, result: connection };
    }
  }
  // Pages reach `allData` through the merge effect below, one render after urql delivers them.
  // Report that gap as pending, not as an empty list.
  const isMergingFirstPage = allData.length === 0 && (connection?.edges.some((edge) => edge?.node) ?? false);
  const isPending = enabled ? (isFetching && !data) || isMergingFirstPage : false;
  const hasNextPage = connection?.pageInfo?.hasNextPage ?? false;
  const endCursor = connection?.pageInfo?.endCursor ?? null;

  // Connection metadata (e.g. totals) is scope-wide and constant across pages. Prefer the latest
  // value; retain the last one through in-flight fetches (when `data` is momentarily undefined) so it
  // never blanks mid-pagination; clear it once a settled response genuinely carries none.
  const currentMetadata = enabled && data && getMetadata ? getMetadata(data) : undefined;
  if (currentMetadata !== undefined) {
    lastMetadataRef.current = currentMetadata;
  } else if (!isFetching) {
    lastMetadataRef.current = undefined;
  }
  const metadata = currentMetadata ?? lastMetadataRef.current;

  // Merge each fetched page into the accumulated list.
  useEffect(() => {
    if (!enabled || isFetching || !connection) return;

    const newItems = connection.edges.filter((edge) => edge?.node).map((edge) => edge.node);

    setAllData((prev) => {
      if (pages.length === 1) return newItems;

      // A collapsed refresh (see refreshAfter) keeps the pages before the target; trim prev to them.
      const keptCount = pages.slice(0, -1).reduce((sum, page) => sum + page.first, 0);
      if (prev.length > keptCount) return [...prev.slice(0, keptCount), ...newItems];

      // Dedup against earlier pages (a stale cursor can re-serve from index 0).
      const existingIds = new Set(prev.map((item) => item.id));
      const uniqueNewItems = newItems.filter((item) => !existingIds.has(item.id));
      return uniqueNewItems.length === 0 ? prev : [...prev, ...uniqueNewItems];
    });

    // Refill an underfilled page (e.g. a cache mutation removed edges) with a network fetch.
    if (newItems.length < currentVariables.first && hasNextPage && refillAttempts < MAX_REFILL_ATTEMPTS) {
      setRefillAttempts((prev) => prev + 1);
      reexecuteQuery({ requestPolicy: "network-only" });
    } else if (newItems.length >= currentVariables.first) {
      setRefillAttempts(0);
    }
  }, [connection, isFetching, pages, enabled, refillAttempts, reexecuteQuery, currentVariables.first, hasNextPage]);

  const fetchNextPage = useCallback(() => {
    if (!enabled || isFetching || !hasNextPage) return;
    setRefillAttempts(0);
    setPages((prev) => [...prev, { first: limit, after: endCursor }]);
  }, [enabled, isFetching, hasNextPage, endCursor, limit]);

  /**
   * Refresh from the page containing `id`: keep the earlier pages, collapse that page and everything
   * after it into one request, and force a network fetch. Preserves the user's position while picking
   * up server changes. When `id` is already gone, refresh the whole range instead.
   */
  const refreshAfter = useCallback(
    (id: string) => {
      if (!enabled) return;

      const itemIndex = allData.findIndex((item) => item.id === id);
      if (itemIndex === -1) {
        const totalSize = pages.reduce((sum, page) => sum + page.first, 0);
        setPages([{ first: totalSize, after: null }]);
        return;
      }

      let itemCount = 0;
      let targetPageIndex = 0;
      for (let i = 0; i < pages.length; i++) {
        if (itemIndex < itemCount + pages[i].first) {
          targetPageIndex = i;
          break;
        }
        itemCount += pages[i].first;
      }

      const collapsedSize = pages.slice(targetPageIndex).reduce((sum, page) => sum + page.first, 0);
      setPages([...pages.slice(0, targetPageIndex), { first: collapsedSize, after: pages[targetPageIndex].after }]);
      reexecuteQuery({ requestPolicy: "network-only" });
    },
    [enabled, allData, pages, reexecuteQuery],
  );

  const refetch = useCallback(() => {
    reexecuteQuery({ requestPolicy: "network-only" });
  }, [reexecuteQuery]);

  return {
    data: allData,
    isFetching,
    isPending,
    error,
    hasNextPage,
    fetchNextPage,
    refreshAfter,
    refetch,
    metadata,
  };
}
